import { Box, Table, TableBody, TableCell, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useRef, useState } from "react";

import { formatCnpj } from "utils/federal-tax-id";

import { Barras, Cabecalho, Cartao, ErroAba, Kpi, SemCadastro, dataSemHora, diaBrt, num, pct, plural, seloDaAba, textoSemCadastro } from "./comum";
import { SeletorPerspectiva, Visao } from "./Perspectiva";
import { Piramide } from "./Piramide";
import { CATALOGO_COMPOSICAO, GRUPO_AFASTAMENTO, rotuloOuCodigo } from "./rotulos";
import { FamiliaDeclarados, FamiliaEnderecaveis, Gente, GenteResposta, Grupo, VisibilidadeResposta } from "./types";
import { QuemVeCadaAba, SeloVisibilidade } from "./Visibilidade";

const SEM_INFO = "Sem informação";

function Situacao({ s }: { s: Gente["situacao"] }) {
  const { ativos, trabalhando, afastados, semFimInformado } = s;
  const parte = (n: number) => (ativos > 0 ? `${(n / ativos) * 100}%` : "0%");
  return (
    <Box data-testid="situacao">
      <Cartao titulo="Situação das pessoas">
        <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(3, 1fr)" }} gap={2}>
          <Kpi rotulo="Pessoas com vínculo ativo" valor={num(ativos)} nota="no eSocial, uma vez cada" />
          <Kpi rotulo="Trabalhando" valor={num(trabalhando)} nota={pct(trabalhando, ativos) || undefined} />
          <Kpi
            rotulo="Afastados"
            valor={num(afastados)}
            nota={[pct(afastados, ativos), "férias não contam"].filter(Boolean).join(" · ")}
          />
        </Box>
        <Box
          role="img"
          aria-label={`${num(trabalhando)} trabalhando, ${num(afastados)} afastados`}
          sx={{ display: "flex", height: 14, borderRadius: 1, overflow: "hidden", bgcolor: "grey.100" }}
        >
          <Box sx={{ width: parte(trabalhando), bgcolor: "#3024E1" }} />
          <Box sx={{ width: parte(afastados), bgcolor: "grey.400" }} />
        </Box>
        {semFimInformado > 0 && (
          <Typography variant="body2" color="text.secondary" data-testid="gente-sem-fim">
            {plural(semFimInformado, "afastado sem data de retorno informada pela folha", "afastados sem data de retorno informada pela folha")}
          </Typography>
        )}
      </Cartao>
    </Box>
  );
}

function Composicao({ g }: { g: Gente }) {
  const linhas = CATALOGO_COMPOSICAO.filter((c) => g.composicao[c.variavel]);
  const [escolhida, setEscolhida] = useState(linhas[0]?.variavel);
  const ativos = g.situacao.ativos;
  const atual = linhas.find((c) => c.variavel === escolhida) ?? linhas[0];
  const valores = (atual && g.composicao[atual.variavel]) || {};
  const peso = (k: string) => {
    const i = atual?.ordem?.indexOf(k) ?? -1;
    return i >= 0 ? i : Number.MAX_SAFE_INTEGER;
  };
  const chaves = Object.keys(valores)
    .filter((k) => k !== "semInformacao")
    .sort((a, b) => peso(a) - peso(b) || a.localeCompare(b, "pt-BR", { numeric: true }));
  const itens = [...chaves, ...("semInformacao" in valores ? ["semInformacao"] : [])].map((k) => ({
    rotulo: k === "semInformacao" ? SEM_INFO : rotuloOuCodigo(atual?.rotulos ?? {}, k),
    n: valores[k],
    nota: [num(valores[k]), pct(valores[k], ativos)].filter(Boolean).join(" · "),
  }));
  return (
    <Box data-testid="composicao">
      <Cartao titulo="Composição" subtitulo={`${ativos === 1 ? "A" : "As"} ${plural(ativos, "pessoa, partida", "pessoas, partidas")} pelo que a folha e o cadastro da empresa informam. Escolha como partir.`}>
        {atual && (
          <ToggleButtonGroup
            exclusive
            size="small"
            aria-label="Partir por"
            value={atual.variavel}
            onChange={(_, v) => v && setEscolhida(v)}
            sx={{ flexWrap: "wrap" }}
          >
            {linhas.map((c) => (
              <ToggleButton key={c.variavel} value={c.variavel}>
                {c.nome}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
        {g.cadastro.disponivel === false && (
          <Box data-testid="composicao-sem-cadastro">
            <Typography variant="body2" color="text.secondary">Sexo, faixa etária e grau de instrução:</Typography>
            <Typography variant="body2" color="text.secondary">{textoSemCadastro(g.cadastro.motivo)}</Typography>
          </Box>
        )}
        <Barras itens={itens} cinza={SEM_INFO} />
        <Typography variant="body2" color="text.secondary">
          "Sem informação" é quem não tem o dado na folha ou no cadastro da empresa.
        </Typography>
      </Cartao>
    </Box>
  );
}

const dias = (d: number | null) =>
  d === null ? (
    <Typography variant="body2" color="text.secondary" component="span">
      sem retorno ainda
    </Typography>
  ) : (
    `${num(Math.round(d))} ${Math.round(d) === 1 ? "dia" : "dias"}`
  );

function Afastados({ a }: { a: Gente["afastados"] }) {
  return (
    <Cartao
      titulo="Afastados hoje"
      subtitulo="Por motivo, com a duração de quem já voltou. Quem está afastado continua com o plano: confira se ele segue ativo."
    >
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small" data-testid="tabela-afastados">
          <TableHead>
            <TableRow>
              <TableCell>Motivo</TableCell>
              <TableCell>Hoje</TableCell>
              <TableCell>Dos que já voltaram, metade voltou em até</TableCell>
              <TableCell>1 em 10 passou de</TableCell>
              <TableCell>Já voltaram</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(Object.keys(GRUPO_AFASTAMENTO) as Grupo[]).map((g) => (
              <TableRow key={g} data-testid={`afastados-${g}`}>
                <TableCell>{GRUPO_AFASTAMENTO[g]}</TableCell>
                <TableCell>{num(a[g].hoje)}</TableCell>
                <TableCell>{dias(a[g].medianaDosQueVoltaram)}</TableCell>
                <TableCell>{dias(a[g].p90DosQueVoltaram)}</TableCell>
                <TableCell>{num(a[g].casos)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Typography variant="body2" color="text.secondary">
        Duração medida nos meses que o eSocial guardado cobre. Férias não contam.
      </Typography>
    </Cartao>
  );
}

function Familia({ f, subtitulo }: { f: FamiliaDeclarados | FamiliaEnderecaveis; subtitulo: string }) {
  const semCad = "semCadastro" in f ? f.semCadastro : 0;
  const soma = f["0"] + f["1"] + f["2"] + f["3+"];
  const item = (rotulo: string, n: number) => ({ rotulo, n, nota: [num(n), pct(n, soma)].filter(Boolean).join(" · ") });
  return (
    <Cartao titulo="Tamanho da família" subtitulo={subtitulo}>
      <Barras
        cor="#B4500A"
        itens={[item("Sem dependente", f["0"]), item("1 dependente", f["1"]), item("2 dependentes", f["2"]), item("3 ou mais", f["3+"])]}
      />
      {semCad > 0 && (
        <Typography variant="body2" color="text.secondary" data-testid="familia-sem-cadastro-n">
          {`${num(semCad)} sem cadastro nesta empresa`}
        </Typography>
      )}
      {soma > 0 && (
        <Typography variant="body2" color="text.secondary" data-testid="familia-media">
          Em média, {(f.dependentes / soma).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} dependentes por titular
        </Typography>
      )}
    </Cartao>
  );
}

const SEM_LEITURA = "Sem endereçáveis lidos para este contrato.";

export function GenteTab({ dados, erro, visibilidade }: { dados?: GenteResposta; erro: boolean; visibilidade: VisibilidadeResposta }) {
  const [visao, setVisao] = useState<Visao>({ tipo: "declarados" });
  const ultimo = useRef<string | null>(null);
  if (erro || !dados) return <ErroAba nome="Minha gente" />;
  const aoLadoDoTitulo = (
    <>
      <SeloVisibilidade selo={seloDaAba(visibilidade, "gente")} />
      <QuemVeCadaAba abas={visibilidade.abas} />
    </>
  );
  const cab = { sobretitulo: "Minha gente", titulo: "Quem é a sua gente hoje", aoLadoDoTitulo };
  if (!dados.disponivel) {
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        <Cabecalho {...cab} dados={{ diaDosNumeros: null, avisos: [] }} />
        <Typography color="text.secondary">Ainda não apurado para esta empresa.</Typography>
      </Box>
    );
  }
  const lista = dados.gente.enderecaveis ?? [];
  const c = visao.tipo === "enderecaveis" ? (lista.find((x) => x.workspaceId === visao.workspaceId) ?? lista[0]) : undefined;
  const escolher = (v: Visao) => {
    if (v.tipo === "enderecaveis") {
      const volta = visao.tipo === "declarados" && lista.some((x) => x.workspaceId === ultimo.current);
      const ws = volta ? (ultimo.current as string) : v.workspaceId;
      ultimo.current = ws;
      setVisao({ tipo: "enderecaveis", workspaceId: ws });
    } else setVisao(v);
  };
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Cabecalho {...cab} dados={{ diaDosNumeros: dados.dataCiclo, avisos: [] }} unidade={`CNPJ ${formatCnpj(dados.cnpj)}`} />
      <Situacao s={dados.gente.situacao} />
      <Composicao g={dados.gente} />
      {dados.gente.cadastro.disponivel === false ? (
        <Cartao titulo="Pirâmide por faixa etária">
          <SemCadastro motivo={dados.gente.cadastro.motivo} testid="piramide-sem-cadastro" />
        </Cartao>
      ) : (
        <>
          <SeletorPerspectiva enderecaveis={lista} visao={visao} onChange={escolher} />
          {c ? (
            c.lidoEm === null ? (
              <Cartao titulo="Pirâmide por faixa etária">
                <Typography variant="body2" color="text.secondary" data-testid="enderecaveis-sem-leitura">{SEM_LEITURA}</Typography>
              </Cartao>
            ) : (
              <>
                {c.piramide && (
                  <Piramide
                    piramide={c.piramide}
                    subtitulo={`As 10 faixas da tabela de preço. Titulares e dependentes que o contrato ${c.nome} aceita.`}
                  />
                )}
                <Typography variant="body2" color="text.secondary">{`Endereçáveis de ${dataSemHora(c.lidoEm)}`}</Typography>
              </>
            )
          ) : (
            dados.gente.piramide && (
              <Piramide
                piramide={dados.gente.piramide}
                subtitulo="As 10 faixas da tabela de preço. Titulares à esquerda, dependentes declarados à direita."
              />
            )
          )}
        </>
      )}
      <Afastados a={dados.gente.afastados} />
      {dados.gente.cadastro.disponivel === false ? (
        <Cartao titulo="Tamanho da família">
          <SemCadastro motivo={dados.gente.cadastro.motivo} testid="familia-sem-cadastro" />
        </Cartao>
      ) : c ? (
        c.lidoEm === null ? (
          <Cartao titulo="Tamanho da família">
            <Typography variant="body2" color="text.secondary" data-testid="enderecaveis-sem-leitura">{SEM_LEITURA}</Typography>
          </Cartao>
        ) : (
          <>
            {c.familia && (
              <Familia f={c.familia} subtitulo={`Titulares pelo número de dependentes que o contrato ${c.nome} aceita.`} />
            )}
            <Typography variant="body2" color="text.secondary">{`Endereçáveis de ${dataSemHora(c.lidoEm)}`}</Typography>
          </>
        )
      ) : (
        dados.gente.familia && <Familia f={dados.gente.familia} subtitulo="Titulares pelo número de dependentes declarados." />
      )}
      <Typography variant="body2" color="text.secondary">
        eSocial lido até {diaBrt(dados.fonteAte)}
        {dados.gente.cadastro.disponivel && (
          <span data-testid="rodape-cadastro">{` · Cadastro de ${dataSemHora(dados.gente.cadastro.lidoEm)}`}</span>
        )}
      </Typography>
    </Box>
  );
}
